/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useLayoutEffect, useRef } from "react";

// react-router components
import { useLocation, useNavigate } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
// import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
// import MDBox from "components/MDBox";

// Material Dashboard 2 PRO React examples
import Sidenav from "utils/Sidenav";

// Material Dashboard 2 PRO React themes
import theme from "assets/theme";

// Material Dashboard 2 PRO React Dark Mode themes
import themeDark from "assets/theme-dark";

// Material Dashboard 2 PRO React routes
import { routes, bottomRoutes } from "routes";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";

// Images
// import brandWhite from "assets/images/logo-ct.png";
import brandWhite from "assets/images/Alysio-logo-white.svg";
// import brandDark from "assets/images/logo-ct-dark.png";
import { AuthProvider } from "auth/useAuth";
import { SubscriptionProvider } from "hooks/useWorkspaceSubscription";
import { useSelector, useDispatch } from "react-redux";
import { AxiosInterceptor, getAPICall, postAPICall } from "lib/callApi";
import { USER_DETAIL, GET_APP_SETTINGS, REPORT_FRONTEND_ERROR } from "lib/constants";
import Loader from "utils/Loader";
import { loginUserAction } from "redux/slice/users";
import { hubAction } from "redux/slice/hub";
import { checkPermission, getFilterDates } from "utils/HelperFunctions";
import { checkUserModuleComponentEnabled } from "utils/Modules";
import { appSettingsActions } from "redux/slice/appSettings";
import { moduleActions } from "redux/slice/modules";
import { homeAction } from "redux/slice/home";
import { notificationAction } from "redux/slice/notifications";
import WebSocket from "websocket";
import { getUserModules } from "apis/module";
import useUserPilot from "hooks/useUserPilot";

import { SnackbarProvider } from "notistack";
import SnackbarCloseButton from "components/SnackbarCloseButton";
import NotistackCustomSnackbar from "components/NotistackCustomSnackbar";
import NotificationModal from "utils/NotificationModal/NotificationModal";
import PublicRoutings from "./routing/publicRoutings";
import "./styles/global.scss";

const MAX_WS_RETRY_COUNT = 5;
const INITIAL_WS_RETRY_DELAY = 3000; // 3 second

export default function App() {
  const wsRef = useRef(null);
  const retryWSCountRef = useRef(0);
  const retryWSTimeoutRef = useRef(null);
  const [controller] = useMaterialUIController();
  const redDispatch = useDispatch();
  const { direction, layout, sidenavColor, darkMode } = controller;
  const [wsClientConnected, setWsClientConnected] = useState(false);
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const storeData = useSelector((state) => state.loginUser.loginUserState);
  const modules = useSelector((state) => state.modules.enabledModules);
  const isProxyLogin = useSelector((state) => state.loginUser.isProxyLogin);
  const subscription = useSelector((state) => state.subscription.subscription);
  const subscriptionLoading = useSelector((state) => state.subscription.loading);
  const appSettings = useSelector((state) => state.appSettings);
  const { loading: notificationsLoading, modalOpen: notificationModalOpen } = useSelector(
    (state) => state.notifications
  );
  const token = localStorage.getItem("token");
  const [isLoading, setIsLoading] = useState(!!token);
  const userType = storeData.type;
  const hasActiveSubscription = subscription?.status === "active";
  const user = storeData;

  useEffect(() => {
    window?.HubSpotConversations?.on?.("widgetClosed", () => {
      window?.HubSpotConversations?.widget?.remove?.();
    });
  }, []);

  useUserPilot(user, subscription);

  const fetchUserInfo = async () => {
    setIsLoading(true);
    const result = await getAPICall(USER_DETAIL, true);
    if (result?.data?.success) {
      redDispatch(
        loginUserAction.getLoginUserData({
          ...result?.data?.data,
          defaultPermissionFilter: result?.data?.data?.defaultPermissionFilter
            ? JSON.stringify(result?.data?.data?.defaultPermissionFilter)
            : null,
          defaultTeamViewFilter: result?.data?.data?.defaultTeamViewFilter
            ? JSON.stringify(result?.data?.data?.defaultTeamViewFilter)
            : null,
          defaultLeaderboardFilter: result?.data?.data?.defaultLeaderboardFilter
            ? JSON.stringify(result?.data?.data?.defaultLeaderboardFilter)
            : null,
        })
      );
    }
    setIsLoading(false);
  };

  const fetchAppSettings = async () => {
    const result = await getAPICall(GET_APP_SETTINGS, true);
    if (result?.data?.success) {
      redDispatch(appSettingsActions.setAppSettings(result?.data));
    } else {
      redDispatch(appSettingsActions.setAppSettings({ settingsDropdownItems: [] }));
    }
  };

  const fetchModules = async () => {
    const result = await getUserModules();
    if (result?.data?.success) {
      redDispatch(moduleActions.setModules(result?.data.modules));
    } else {
      redDispatch(moduleActions.setModules({ modules: [] }));
    }
  };

  const checkUnseenBadges = async () => {
    if (!notificationsLoading && !notificationModalOpen) {
      redDispatch(notificationAction.checkUnseenBadgesAction());
    }
  };

  const connectWebSocket = () => {
    const W3CWebSocket = WebSocket.w3cwebsocket;

    wsRef.current = new W3CWebSocket(process.env.REACT_APP_WS_URL, "echo-protocol");

    wsRef.current.onerror = (err) => {
      // eslint-disable-next-line no-console
      console.log("Websocket client failed to connect", err);
      try {
        if (err?.message) {
          postAPICall(REPORT_FRONTEND_ERROR, true, {
            err: {
              message: err?.message,
              stack: err?.stack,
              name: err?.name,
              cause: err?.cause,
            },
            clientDetails: {
              userId: user.id,
              companyId: user.companyId,
              userCompanyLinkId: user.userCompanyLinkId,
              name: user.name,
            },
          });
        }
      } catch (reportingError) {
        console.log("Error reporting frontend error", reportingError);
      }
    };

    wsRef.current.onopen = () => {
      // eslint-disable-next-line no-console
      console.log("Websocket client connected");
      setWsClientConnected(true);
      // Reset retry count on successful connection
      retryWSCountRef.current = 0;
    };

    wsRef.current.onclose = (event) => {
      // eslint-disable-next-line no-console
      console.log("Websocket client disconnected", event);
      setWsClientConnected(false);

      // Don't retry if the connection was closed intentionally
      if (event.code === 1000) {
        return;
      }

      // Implement exponential backoff for retry
      if (retryWSCountRef.current < MAX_WS_RETRY_COUNT) {
        // eslint-disable-next-line no-restricted-properties, prefer-exponentiation-operator
        const delay = INITIAL_WS_RETRY_DELAY * Math.pow(2, retryWSCountRef.current);
        console.log(
          `Retrying connection in ${delay}ms. Attempt ${
            retryWSCountRef.current + 1
          }/${MAX_WS_RETRY_COUNT}`
        );

        retryWSTimeoutRef.current = setTimeout(() => {
          retryWSCountRef.current += 1;
          connectWebSocket();
        }, delay);
      } else {
        console.log("Max retry attempts reached. Please refresh the page to reconnect.");
      }
    };

    wsRef.current.onmessage = (e) => {
      if (typeof e.data === "string") {
        const msg = JSON.parse(e.data);
        if (msg.msgType === "refreshKpis") {
          const dates = getFilterDates("Today", user.timezone);
          redDispatch(homeAction.getKpiEntrySaga(dates));
          redDispatch(homeAction.refetchCompetitions());
        } else if (msg.msgType === "refreshLeaderboardWidgets") {
          redDispatch(homeAction.refreshLeaderboardWidgets({ kpiActionId: msg.data.kpiActionId }));
        } else if (msg.msgType === "refreshKpiWidgets") {
          redDispatch(homeAction.refreshKpiWidgets({ kpiActionId: msg.data.kpiActionId }));
        } else if (msg.msgType === "refreshAchievements") {
          redDispatch(homeAction.refreshAchievements());
        } else if (msg.msgType === "refreshWorkingDays") {
          redDispatch(loginUserAction.updateWorkingDays(msg.data.workingDays));
        } else if (msg.msgType === "addWinWireReply") {
          redDispatch(hubAction.addWinWireReply(msg.data));
        } else if (msg.msgType === "editWinWireReply") {
          redDispatch(hubAction.editWinWireReply(msg.data));
        } else if (msg.msgType === "addWinWireReaction" && msg.data) {
          redDispatch(hubAction.addWinWireReaction(msg.data));
        } else if (msg.msgType === "removeWinWireReply" && msg.data) {
          redDispatch(hubAction.removeWinWireReply(msg.data));
        } else if (msg.msgType === "removeWinWireReaction" && msg.data) {
          redDispatch(hubAction.removeWinWireReaction(msg.data));
        } else if (msg.msgType === "addWinWireMessage" && msg.data) {
          redDispatch(hubAction.addWinWireMessage(msg.data));
        } else if (msg.msgType === "removeWinWireMessages" && msg.data) {
          redDispatch(hubAction.removeWinWireMessages(msg.data));
        } else if (msg.msgType === "awardBadges" && msg.data) {
          redDispatch(notificationAction.showNotification(msg.data));
        } else if (msg.msgType === "refreshModules") {
          if (token && hasActiveSubscription && userType !== "admin") {
            fetchModules();
          }
        } else if (msg.msgType === "newNotification") {
          redDispatch(homeAction.newNotification(msg.data));
        }
      }
    };
  };

  // Change the openConfigurator state
  // const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // handle websocket stuff
  useEffect(() => {
    if (!wsClientConnected && user?.id) {
      connectWebSocket();
    } else if (wsClientConnected && !user?.id) {
      // Clear any pending retry attempts
      if (retryWSTimeoutRef.current) {
        clearTimeout(retryWSTimeoutRef.current);
      }
      wsRef?.current?.close?.(1000, "User logged out");
    }

    // Cleanup function
    return () => {
      if (retryWSTimeoutRef.current) {
        clearTimeout(retryWSTimeoutRef.current);
      }
    };
  }, [user, wsClientConnected]);

  useEffect(() => {
    let intervalId = null;

    if (wsClientConnected) {
      intervalId = setInterval(() => {
        try {
          if (wsRef?.current?.readyState === 1 && user?.id && user?.companyId) {
            const message = {
              msgType: "metadata",
              msgValue: {
                userCompanyLinkId: user.userCompanyLinkId,
                userId: user.id,
                companyId: user.companyId,
              },
            };

            wsRef.current.send(JSON.stringify(message));
            clearInterval(intervalId);
            intervalId = null;
          }
        } catch (err) {
          console.log("Something went wrong with sending metadata to websocket");
          console.error(err);
        }
      }, 500);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [wsClientConnected, user]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // eslint-disable-next-line consistent-return
  useLayoutEffect(() => {
    const proxyUserState = JSON.parse(localStorage.getItem("proxyUserState"));
    const localStorageToken = localStorage.getItem("token");
    if (
      !isProxyLogin &&
      !proxyUserState &&
      localStorageToken &&
      !storeData.emailVerified &&
      !pathname.includes("verify-email")
    ) {
      return navigate("/verify-email", { replace: true });
    }

    if (
      !isProxyLogin &&
      !proxyUserState &&
      localStorageToken &&
      storeData.disabled &&
      !pathname.includes("workspace")
    ) {
      return navigate("/workspace", { replace: true });
    }

    if (
      !localStorageToken &&
      !user.id &&
      user.email &&
      user.emailVerified &&
      !pathname.includes("workspace") &&
      !pathname.includes("create-password")
    ) {
      return navigate("/workspace", { replace: true });
    }

    if (
      pathname.includes("verify-email") ||
      pathname.includes("workspace") ||
      pathname.includes("create-password")
    )
      // eslint-disable-next-line consistent-return
      return;

    if (
      !pathname.includes("change-password") &&
      !pathname.includes("create-password") &&
      !pathname.includes("forgot-password") &&
      !pathname.includes("invalid-invitation") &&
      (localStorageToken === null || localStorageToken.length === 0)
    ) {
      navigate("/sign-in", { replace: true });
    }
    if (
      pathname.includes("sign-in") &&
      (localStorageToken === null || localStorageToken.length === 0)
    ) {
      navigate(`/sign-in${search}`, { replace: true });
    }

    if (
      pathname.includes("sign-up") &&
      (localStorageToken === null || localStorageToken.length === 0)
    ) {
      navigate("/sign-up", { replace: true });
    }
    if (
      pathname.includes("proxy-login") &&
      proxyUserState &&
      proxyUserState?.user?.type === "admin" &&
      (localStorageToken === null || localStorageToken.length === 0)
    ) {
      navigate("/proxy-login", { replace: true });
    }

    // in case the user has been created fully but has no active workspace
    if (
      user.email &&
      user.emailVerified &&
      !user.hasActiveWorkspaces &&
      user.type !== "admin" &&
      !pathname.includes("workspace") &&
      !pathname.includes("create-password") &&
      !pathname.includes("subscription-plans")
    ) {
      return navigate("/workspace", { replace: true });
    }

    // handle for inactive subscription
    if (
      user.email &&
      user.emailVerified &&
      user.hasActiveWorkspaces &&
      !hasActiveSubscription &&
      !subscriptionLoading &&
      user.type !== "admin" &&
      !pathname.includes("subscription-plans")
    ) {
      return navigate("/subscription-plans");
    }
  }, [pathname, isProxyLogin, hasActiveSubscription, subscriptionLoading, user]);

  useEffect(() => {
    if (token) {
      fetchUserInfo();
    }
  }, [token]);

  useEffect(() => {
    if (token && hasActiveSubscription && userType !== "admin") {
      fetchAppSettings();
      fetchModules();
      checkUnseenBadges();
    }
  }, [token, hasActiveSubscription, userType]);

  useEffect(() => {
    const eventHandler = () => {
      fetchAppSettings();
    };

    window.addEventListener("permissionsUpdated", eventHandler);
    return () => window.removeEventListener("permissionsUpdated", eventHandler);
  }, []);

  const getRoutes = (allRoutes) => {
    const routings = allRoutes
      .filter((route) => {
        // Component check
        if (
          route.module_component &&
          route.module &&
          !checkUserModuleComponentEnabled(modules, route.module, route.component)
        ) {
          return false;
        }

        // Module check
        if (route.module && !checkUserModuleComponentEnabled(modules, route.module)) {
          return false;
        }

        if (userType !== "user" || !route.permissionName) {
          return true;
        }

        if (userType === "user" && Array.isArray(route.permissionName)) {
          let permissionAllowed = false;
          // eslint-disable-next-line no-restricted-syntax, no-unsafe-optional-chaining
          for (const permission of route?.permissionName) {
            if (checkPermission(permission, storeData?.userPermission)) {
              permissionAllowed = true;
              break;
            }
          }
          return permissionAllowed;
        }

        if (
          userType === "user" &&
          route.permissionName &&
          checkPermission(route.permissionName, storeData?.userPermission)
        ) {
          return true;
        }

        return false;
      })
      .map((route) => {
        const clonedRoute = { ...route };
        const collapse = [];
        if (clonedRoute.type === "collapse") {
          clonedRoute.collapse.forEach((nestedRoute) => {
            if (
              nestedRoute.accessby?.includes(userType) &&
              userType === "user" &&
              Array.isArray(nestedRoute.permissionName)
            ) {
              let permissionAllowed = false;
              // eslint-disable-next-line no-restricted-syntax, no-unsafe-optional-chaining
              for (const permission of nestedRoute?.permissionName) {
                if (checkPermission(permission, storeData?.userPermission)) {
                  permissionAllowed = true;
                  break;
                }
              }
              if (permissionAllowed) {
                collapse.push(nestedRoute);
              }
            } else if (
              nestedRoute.accessby?.includes(userType) &&
              ((userType === "user" &&
                nestedRoute.permissionName &&
                checkPermission(nestedRoute.permissionName, storeData?.userPermission)) ||
                userType !== "user" ||
                !nestedRoute.permissionName)
            ) {
              collapse.push(nestedRoute);
            }
          });
          clonedRoute.collapse = collapse;
        }

        if (clonedRoute.type === "button" && clonedRoute.key === "settings") {
          const showSettingsCog = !!(
            !appSettings.loadingAppSettings && appSettings.settingsDropdownItems?.length
          );
          return showSettingsCog ? clonedRoute : null;
        }

        if (clonedRoute.route || clonedRoute.type === "button") {
          return route?.accessby?.includes(userType)
            ? clonedRoute
            : // <Route exact path={route.route} element={route.component} key={route.key} />
              null;
        }

        return null;
      });

    const removeUnmatched = routings.filter((element) => element !== null);
    return removeUnmatched;
  };

  // Refetch page data on stale refocus
  useEffect(() => {
    let timeStart;

    const reloadData = () => {
      if (process.env.REACT_APP_DISABLE_BACKGROUND_REFRESH || isProxyLogin) return;
      try {
        const visibilityState = document?.visibilityState;

        if (!visibilityState) return;

        if (visibilityState === "hidden") {
          timeStart = Date.now();
        } else if (visibilityState === "visible" && timeStart) {
          if (Date.now() - timeStart > 90000) {
            window.location.reload();
          } else {
            timeStart = null;
          }
        }
      } catch (err) {
        console.log("Err reloading page");
        console.log(err);
      }
    };

    document?.addEventListener?.("visibilitychange", reloadData);

    return () => {
      document?.removeEventListener?.("visibilitychange", reloadData);
    };
  }, [isProxyLogin]);

  return (
    <AxiosInterceptor>
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <SnackbarProvider
          autoHideDuration={5000}
          maxSnack={3}
          // eslint-disable-next-line react/no-unstable-nested-components
          action={(snackbarKey) => <SnackbarCloseButton snackbarKey={snackbarKey} />}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          Components={{
            custom: NotistackCustomSnackbar,
          }}
        >
          <CssBaseline />
          {isLoading && <Loader loading={isLoading} />}
          {!isLoading && (
            <AuthProvider>
              <SubscriptionProvider token={token}>
                {token !== null &&
                  token.length > 0 &&
                  layout === "dashboard" &&
                  pathname !== "/404" &&
                  pathname !== "/subscription-plans" && (
                    <Sidenav
                      color={sidenavColor}
                      brand={brandWhite}
                      routes={getRoutes(routes)}
                      bottomRoutes={getRoutes(bottomRoutes)}
                    />
                  )}
                <PublicRoutings />
                <NotificationModal />
              </SubscriptionProvider>
            </AuthProvider>
          )}
        </SnackbarProvider>
      </ThemeProvider>
    </AxiosInterceptor>
  );
}
